import { Badge, EmptyState, Skeleton } from '@h2hspark/ui';
import { Info, ReceiptText } from 'lucide-react';
import * as React from 'react';
import { Link, useParams } from 'react-router-dom';

import { PageBackLink } from '@/member/page-back-link';

import { formatTicketDateTime, ticketPath } from '../event-tickets/constants';

import { useMyOrder } from './api';
import {
  ORDER_DETAIL_HEADING,
  ORDER_NOT_FOUND_DESCRIPTION,
  ORDER_NOT_FOUND_TITLE,
  ORDER_STATUS_BADGE_TONES,
  PAYMENT_HEADING,
  REFUNDED_LABEL,
  REFUND_POLICY_HEADING,
  SERVICE_FEE_DISCLOSURE_TEST_ID,
  TICKETS_AND_ORDERS_PATH,
  TICKETS_AND_ORDERS_HEADING,
  TRANSACTION_HEADING,
  VIEW_RECEIPT_LABEL,
  VIEW_TICKET_LABEL,
  chargedOnLine,
  orderNumberLine,
  orderReceiptPath,
  savedCardLine,
  whenAndWhereLine,
} from './constants';
import { OrderItemisation } from './order-itemisation-card';
import type { MemberOrderDetail } from './types';

/**
 * **Order details** (`MEM-022` AC-4) — one purchase, in full.
 *
 * ## What AC-4 lists, and where each piece comes from
 *
 * *"the status badge and order number; the event name; `{date and time} · {venue}`; a Transaction
 * card with the itemised lines — the ticket, the Service fee and 'Total', the total paid including
 * the service fee"*. Every one of them is a field on the one payload
 * `GET /api/event-orders/orders/:orderId` serves, so this screen composes nothing and fetches
 * nothing else:
 *
 * - **the badge** — `order.statusLabel`, served. This screen does not switch on a status, so a
 *   refunded order reads "Refunded" without an edit here, which is the half of `MEM-023` AC-5
 *   that this story owns.
 * - **the order number** — `order.orderNumber`, which is the registration's confirmation code:
 *   the same number the ticket prints and the confirmed panel prints, never the order's own key.
 * - **the date** — rendered by `formatTicketDateTime`, the ticket's formatter, in the **event's**
 *   timezone. Reused rather than rewritten: a second derivation is how a member's ticket and their
 *   order come to name two different days for one event.
 * - **the Transaction card** — `OrderItemisation`, the very same component checkout and the
 *   reservation confirmation render. `MEM-020` AC-3's mechanism, extended to a third surface: the
 *   lines are not written to agree, they are the same array.
 * - **the Refunds section** — the terms **this order** agreed, off its own snapshot, and beneath
 *   them the fee-forfeit disclosure the member was shown before they paid, repeated on the order
 *   record in the same served words (`MEM-050` AC-3). The prototype draws a refund sentence here
 *   too, and draws the wrong one — a sample policy as a platform rule — which is why both are
 *   served rather than written.
 *
 * ## The one clause that used to be a conflict, and the ruling that closed it
 *
 * AC-4 used to end *"and 'Total paid'"*. The total's label is `TOTAL_LABEL` on the server and
 * reads **`Total`**, because `MEM-020` AC-3 requires checkout and this screen to carry the same
 * label and *"'Total paid' cannot be the survivor: at checkout nothing has been paid"*.
 *
 * That was a **genuine conflict between two stories' acceptance criteria**, recorded before this
 * screen existed — in `MEM-020`'s Notes, in `order-itemisation.ts` beside the constant, and in
 * `event-orders/README.md` — and left for a PM rather than resolved silently in either direction.
 * **Sumit ruled on 2026-08-29: the label is "Total".** `MEM-022` AC-4 and AC-5 were reworded to
 * say so and are now tagged; nothing in this file changed, which was the point of leaving it.
 * Whichever word a later decision picks, it wins in `TOTAL_LABEL` and all three screens follow.
 *
 * ## Ownership is the server's answer and this screen does not second-guess it
 *
 * `MEM-022` AC-7. An order that is not this member's answers `404`, indistinguishable from one
 * that never existed, so there are exactly two states below: the order, or the not-found empty
 * state. There is deliberately no "you may not see this" — the client is never told that, and a
 * screen that could render it would be a screen that knew the order existed.
 */
export function OrderDetailPage(): React.ReactElement {
  const { orderId = '' } = useParams<{ orderId: string }>();
  const query = useMyOrder(orderId, { enabled: true });

  if (query.isPending) {
    return (
      <OrderDetailShell>
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-40 w-full rounded-lg" />
      </OrderDetailShell>
    );
  }

  if (query.error !== null || query.data === undefined) {
    return (
      <OrderDetailShell>
        <EmptyState
          icon={<ReceiptText className="h-6 w-6" aria-hidden />}
          title={ORDER_NOT_FOUND_TITLE}
          description={ORDER_NOT_FOUND_DESCRIPTION}
        />
      </OrderDetailShell>
    );
  }

  const { order } = query.data;
  const when = formatTicketDateTime({ startsAt: order.startsAt, timezone: order.timezone });
  const whenAndWhere = whenAndWhereLine(when, order.venueName);

  return (
    <OrderDetailShell>
      <header className="space-y-1">
        <p className="h2h-eyebrow">{ORDER_DETAIL_HEADING}</p>
        <div className="flex items-center gap-2">
          <Badge variant={ORDER_STATUS_BADGE_TONES[order.status]}>{order.statusLabel}</Badge>
          <span className="text-sm text-muted-foreground">
            {orderNumberLine(order.orderNumber)}
          </span>
        </div>
        <h1 className="font-brand text-xl font-bold text-foreground">{order.eventName}</h1>
        {whenAndWhere === null ? null : <p className="text-sm text-text-body">{whenAndWhere}</p>}
      </header>

      <section className="space-y-2">
        <h2 className="h2h-eyebrow">{TRANSACTION_HEADING}</h2>
        {/* The one itemiser. Nothing in this file maps `order.lines` — see
            `one-itemisation-two-screens.test.tsx`, which asserts that only the card does.

            The surface is this screen's since the itemiser stopped drawing one of its own: it
            renders the lines and the callers decide what they sit on, which is what let the
            printable receipt stop passing `border-0 p-0` to undo a card it never wanted. */}
        <div className="rounded-xl border border-border-subtle bg-card p-4 shadow-sm sm:p-5">
          <OrderItemisation order={order.order} />
        </div>
      </section>

      <PaymentCard order={order} />

      {/* The terms this order agreed and the disclosure that goes with them — `MEM-050` AC-3's
          "repeated on the order record". Both served; neither composed here. Drawn as one region
          under the same heading the checkout and the confirmation use, so the three screens
          describe one deal in one place. */}
      <section className="space-y-1.5">
        <h2 className="h2h-eyebrow">{REFUND_POLICY_HEADING}</h2>
        <p className="flex items-start gap-2 text-sm text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>{order.refundPolicy?.sentence ?? order.refundPolicyNotice}</span>
        </p>
        {order.feeDisclosure === null ? null : (
          <p data-testid={SERVICE_FEE_DISCLOSURE_TEST_ID} className="pl-6 text-sm text-text-body">
            {order.feeDisclosure}
          </p>
        )}
      </section>

      <div className="space-y-2">
        <Link
          to={orderReceiptPath(order.orderId)}
          className="flex w-full items-center justify-center rounded-pill bg-brand px-4 py-2.5 font-brand text-sm font-bold text-primary-foreground"
        >
          {VIEW_RECEIPT_LABEL}
        </Link>
        <Link
          to={ticketPath(order.eventId)}
          className="flex w-full items-center justify-center rounded-pill border border-border px-4 py-2.5 font-brand text-sm font-bold text-foreground"
        >
          {VIEW_TICKET_LABEL}
        </Link>
      </div>

      {/*
        There is no "Cancel ticket & request refund" control here, and its absence is `MEM-023`.

        The prototype's order detail carries one, in danger red, under the sample sentence "Full
        refunds up to 48 hours before the event" — which is the defect `MEM-TKT-013`'s own
        correction records: one organizer's policy presented as a platform rule. `MEM-023` owns
        member-initiated cancellation and is `draft`; this story's Out of scope names it. Drawing
        the button here would be the first half of a feature the story says is not built.
      */}
    </OrderDetailShell>
  );
}

/**
 * How the money moved (`MEM-TKT-013`'s Payment card).
 *
 * The card line is `savedCardLine`, the same helper the checkout's saved-card list renders, so
 * "Visa ending 4242" is one sentence across the two screens rather than two. A refunded order adds
 * a line stating what came back — `order.payment.refunded` is the server's own `formatCents`
 * rendering, never an amount this screen computed.
 *
 * **No "Confirmation sent to {email}" row**, which the prototype carries. The address it would
 * print is the member's own, so it discloses nothing — but the claim it makes is that a message
 * was delivered, and this screen has no delivery record to read: `COM-002` queues the confirmation
 * and `EVT-RPT-011` keeps delivery statistics out of the data model entirely. A line asserting a
 * send that this product cannot confirm is the kind of thing a member contacts support about.
 */
function PaymentCard({ order }: { readonly order: MemberOrderDetail }): React.ReactElement | null {
  const { payment } = order;
  const paidOn = payment.paidAt === null ? null : formatPaymentDate(payment.paidAt, order.timezone);
  if (payment.card === null && paidOn === null && payment.refunded === null) return null;

  return (
    <section className="space-y-2">
      <h2 className="h2h-eyebrow">{PAYMENT_HEADING}</h2>
      <div className="space-y-1 rounded-lg border border-border bg-card p-4">
        {payment.card === null ? null : (
          <p className="font-brand text-sm font-bold text-foreground">
            {savedCardLine(payment.card.brand, payment.card.last4)}
          </p>
        )}
        {paidOn === null ? null : (
          <p className="text-sm text-muted-foreground">{chargedOnLine(paidOn)}</p>
        )}
        {payment.refunded === null ? null : (
          <p className="text-sm text-text-body">
            {REFUNDED_LABEL} {payment.refunded}
          </p>
        )}
      </div>
    </section>
  );
}

/**
 * A payment instant, rendered as a date in the event's own zone.
 *
 * The **event's** zone rather than the member's, deliberately, and it is the less obvious choice:
 * a charge is a fact about the member's card, so their own zone has a claim on it. But every other
 * instant on this screen and on the receipt is rendered in the event's zone, and a document that
 * mixed two zones without saying which is which is worse than one that picks one and is
 * consistent. `formatTicketDateTime` carries the zone abbreviation, so the receipt says which.
 *
 * `null` for an instant that cannot be parsed, so the line is dropped rather than reading
 * "Invalid Date" on a document somebody files.
 */
function formatPaymentDate(instant: string, timezone: string | null): string | null {
  return formatTicketDateTime({ startsAt: instant, timezone });
}

function OrderDetailShell({
  children,
}: {
  readonly children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="space-y-4">
      <PageBackLink to={TICKETS_AND_ORDERS_PATH}>{TICKETS_AND_ORDERS_HEADING}</PageBackLink>
      {children}
    </div>
  );
}
